import {
  type ID,
  ProjectActions,
  type TagItem,
} from '@octopus-do/octopus-project-core'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type {
  ChangeTagOrderPayload,
  UpdateTagColorPayload,
  UpdateTagTitlePayload,
} from '@store/reducers/tags/types'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import * as ActionTypes from '@storeOld/actions'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import type { Dispatch } from 'react'
import type { Action } from 'redux'

import { OpenConfirmation } from '@/store'

// Returns the id of the new tag, e.g. to put it on the page right away.
// Without a color the project default one is used.
export const thunkAddTag =
  (title: string, color: ID | null = null): AppThunk<ID> =>
  (dispatch, getState) => {
    const id = getId()
    const colorId =
      color ?? migrationProjectSelector.getProjectDefaultColorId(getState())

    const tag: TagItem = {
      id,
      title,
      colorId,
      nodes: [],
    }

    dispatch({
      type: 'COMMIT_PROJECT_REQUEST',
      payload: [
        ProjectActions.tags.add({
          items: [tag],
        }),
      ],
    })

    return id
  }

export const changeTagOrder =
  (payload: ChangeTagOrderPayload) => (dispatch: Dispatch<any>) => {
    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.tags.updateOrder({
          id: payload.id,
          order: payload.index,
        }),
      ],
    })
  }

export const updateTagTitle =
  ({ id, title }: UpdateTagTitlePayload): AppThunk =>
  (dispatch) => {
    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.tags.updateTitle({
          id,
          title,
        }),
      ],
    })
  }

export const updateTagColor =
  ({ id, colorId }: UpdateTagColorPayload): AppThunk =>
  (dispatch) => {
    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.tags.updateColor({
          id,
          colorId,
        }),
      ],
    })
  }

// Without nodes the selected ones get the tag.
export const addNodeToTag =
  (tagId: string, nodes: ID[] | null = null): AppThunk =>
  (dispatch, getState) => {
    const ids = nodes ?? getState().canvas.node.selectedNodes

    if (!ids.length) {
      return
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ...ids.map((id) =>
          ProjectActions.tags.addNode({
            id: tagId,
            item: id,
          })
        ),
      ],
    })
  }

export const deleteNodeFromTag =
  (tagId: string, nodes: ID[] | null = null): AppThunk =>
  (dispatch, getState) => {
    const ids = nodes ?? getState().canvas.node.selectedNodes

    if (!ids.length) {
      return
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ...ids.map((id) =>
          ProjectActions.tags.deleteNode({
            id: tagId,
            item: id,
          })
        ),
      ],
    })
  }

export const deleteTag =
  (id: string): AppThunk =>
  (dispatch, getState) => {
    const { canvas } = getState()

    const used = canvas.tags.selectedTags.includes(id)

    const then = [
      commitProjectRequest(
        ProjectActions.tags.delete({
          ids: [id],
        })
      ),
      used && { type: CanvasActionTypes.REMOVE_FILTER_TAG, payload: id },
    ].filter(Boolean) as Action[]

    dispatch(
      OpenConfirmation({
        title: 'Delete tag',
        message: 'Tag will be permanently deleted. Are you sure?',
        label: 'Delete',
        then,
      })
    )
  }
